import React, { useState, useEffect, useCallback } from 'react';
import OptimizedImage, { getOptimizedImageUrl } from './OptimizedImage';

// Generic placeholder SVG (no logo)
const genericPlaceholder = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVIMTc1VjEyNUgxMjVWNzVaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMzcuNSAxMDAuNUwxNDIuNSAxMDUuNUwxNTcuNSA5MC41TDE2Mi41IDk1LjVMMTQ3LjUgMTEwLjVMMTM3LjUgMTAwLjVaIiBmaWxsPSIjNkI3MjgwIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOUNBM0FGIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiPkxvYWRpbmcuLi48L3RleHQ+Cjwvc3ZnPgo=";

/**
 * PropertyImageWithFallback Component
 * Specialized component for property images with retry logic and caching
 * Now optimized for fast loading with caching and lazy loading
 */
export const PropertyImageWithFallback = ({ 
  src, 
  alt = "Property", 
  className = "", 
  fallbackSrc = null, // No default fallback - will use generic placeholder
  priority = false,
  lazy = true,
  progressive = true,
  width,
  height,
  quality = 80,
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get optimized image URL
  const optimizedSrc = getOptimizedImageUrl(src, { width, height, quality });

  const handleImageError = useCallback(() => {
    // Don't set error immediately - let retry logic handle it
    // Only set error after all retries are exhausted
    setIsLoading(false);
  }, []);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setImageError(false);
  }, []);

  // If no src provided, show generic placeholder (not logo)
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`} {...props}>
        <div className="text-gray-500 text-center">
          <div className="text-4xl mb-2">🏠</div>
          <div className="text-sm">No image</div>
        </div>
      </div>
    );
  }

  return (
    <OptimizedImage
      src={optimizedSrc}
      alt={alt}
      className={className}
      fallbackSrc={fallbackSrc || genericPlaceholder}
      priority={priority}
      lazy={lazy}
      progressive={progressive}
      onLoad={handleImageLoad}
      onError={handleImageError}
      placeholder={true}
      {...props}
    />
  );
};

/**
 * PropertyImageCarousel Component
 * For carousel/slider images with fallback
 * Now optimized for fast loading
 */
export const PropertyImageCarousel = ({ 
  images = [], 
  currentIndex = 0,
  className = "",
  fallbackSrc = null,
  priority = false,
  width,
  height,
  quality = 80,
  ...props 
}) => {
  const [imageErrors, setImageErrors] = useState({});

  const handleImageError = useCallback((index) => {
    // Don't set error immediately - let retry logic handle it
    // Only set error after all retries are exhausted
  }, []);

  const currentImage = images[currentIndex];
  const hasError = imageErrors[currentIndex];

  // Get optimized image URL
  const optimizedSrc = getOptimizedImageUrl(currentImage, { width, height, quality });

  if (!currentImage || hasError) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`} {...props}>
        <div className="text-gray-500 text-center">
          <div className="text-4xl mb-2">🏠</div>
          <div className="text-sm">No image</div>
        </div>
      </div>
    );
  }

  return (
    <OptimizedImage
      src={optimizedSrc}
      alt={`Property image ${currentIndex + 1}`}
      className={className}
      fallbackSrc={fallbackSrc || genericPlaceholder}
      priority={priority}
      lazy={false} // Don't lazy load current carousel image
      onError={() => handleImageError(currentIndex)}
      width={width}
      height={height}
      quality={quality}
      {...props}
    />
  );
};
